
import Vue from 'vue';
import Component from "vue-class-component";
import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
import HeadingWave from "@/features/shared/components/HeadingWave.vue";
import AdministrationDetailviewLayout from "@/features/administration/AdministrationDetailviewLayout.vue";
import FileUploader from "@/components/FileUploader.vue";
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
import ErrorsSummary from "@/components/ErrorsSummary.vue";
import {Route} from "vue-router";
import {Prop, Watch} from "vue-property-decorator";
import toasts from "@/components/Toasts";
import { settings } from '@/main';
import { cultureService } from '@/CultureService';
import {
    SysAdminCertificateTypesClient,
    CertificateTypeLightModel,
    SysAdminCertificateTypeTemplatesClient,
    UpsertCertificateTypeTemplateRequest,
    MultilanguageModel,
    TempFileModel,
    ResultError
} from "@/api-client";

// Local model for form binding
interface CertificateTypeTemplateFormModel {
    id?: string;
    name: string;
    certificateTypeIds: string[];
    descriptionEt: string;
    descriptionEn: string;
    version: string;
    isActive: boolean;
}

interface Props {
    preloadedTemplate?: CertificateTypeTemplateFormModel;
    certificateTypes: CertificateTypeLightModel[];
    existingFileUrl?: string | null;
}

@Component({
    components: {
        SectionPageHeading,
        HeadingWave,
        AdministrationDetailviewLayout,
        FileUploader,
        HeadingGroup,
        ErrorsSummary
    },
    async beforeRouteEnter(to: Route, from: Route, next: Function) {
        const props = to.meta!.props as Props;

        // Load certificate types for dropdown
        props.certificateTypes = await new SysAdminCertificateTypesClient().list();

        if (to.params.id) {
            // Load existing template
            const templateClient = new SysAdminCertificateTypeTemplatesClient();
            const template = await templateClient.get(to.params.id);

            props.preloadedTemplate = {
                id: template.id,
                name: template.name,
                certificateTypeIds: template.certificateTypeIds || [],
                // Now we get proper multilanguage description from the API
                descriptionEt: template.description?.et || "",
                descriptionEn: template.description?.en || "",
                version: template.templateVersion,
                isActive: template.isActive
            };

            props.existingFileUrl = template.templateFileUrl;
        }

        next();
    }
})
export default class UpsertCertificateTypeTemplate extends Vue implements Props {
    @Prop()
    preloadedTemplate?: CertificateTypeTemplateFormModel;

    @Prop({ required: true })
    certificateTypes!: CertificateTypeLightModel[];

    @Prop()
    existingFileUrl?: string | null;

    model: CertificateTypeTemplateFormModel = {
        name: "",
        certificateTypeIds: [],
        descriptionEt: "",
        descriptionEn: "",
        version: "",
        isActive: true
    };

    selectedCertificateTypes: CertificateTypeLightModel[] = [];

    templateFiles: TempFileModel[] = [];

    saving = false;
    changingActiveState = false;
    validationErrors: ResultError = ResultError.createEmpty();
    validation = {
        name: null as boolean | null,
        certificateTypeIds: null as boolean | null
    };

    get isEditMode(): boolean {
        return !!this.$route.params.id;
    }

    get certificateTypeOptions() {
        return this.certificateTypes.map(ct => ({
            value: ct.id,
            text: ct.name
        }));
    }


    get currentCulture(): string {
        return cultureService.currentCulture;
    }

    created() {
        if (this.preloadedTemplate) {
            this.model = {
                ...this.preloadedTemplate
            };
            // Populate selected certificate types for multiselect
            this.selectedCertificateTypes = this.certificateTypes.filter(
                ct => this.model.certificateTypeIds.includes(ct.id)
            );
        }
    }

    @Watch('selectedCertificateTypes')
    onSelectedCertificateTypesChange(newTypes: CertificateTypeLightModel[]) {
        this.model.certificateTypeIds = newTypes.map(ct => ct.id);
        if (newTypes.length > 0) {
            this.validation.certificateTypeIds = true;
        }
    }

    @Watch('templateFiles')
    async onTemplateFilesChange(newFiles: TempFileModel[]) {
        if (newFiles.length > 0) {
            // Auto-increment version when file is uploaded
            if (this.isEditMode && this.model.version) {
                // Extract number from existing version (e.g., "v1" -> 1)
                const match = this.model.version.match(/^v(\d+)$/);
                if (match) {
                    const currentVersion = parseInt(match[1]);
                    this.model.version = `v${currentVersion + 1}`;
                } else {
                    // If current version doesn't match pattern, start with v2
                    this.model.version = 'v2';
                }
            } else if (!this.isEditMode) {
                // For new templates, start with v1
                this.model.version = 'v1';
            }
        }
    }


    getValidationState(field: 'name' | 'certificateTypeIds'): boolean | null {
        return this.validation[field];
    }

    getProcessedDownloadUrl(): string {
        // Use the public download endpoint that replaces placeholders with values
        if (this.model.id) {
            // Add timestamp to avoid cache
            return `${settings.misApiBaseUrl}/api/certificate-type-templates/${this.model.id}/download?t=${Date.now()}`;
        }
        // Fallback to existing file URL if no ID (shouldn't happen in edit mode)
        return this.existingFileUrl || '';
    }

    downloadTemplate() {
        // Use iframe to trigger download without navigation
        const url = this.getProcessedDownloadUrl();
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = url;
        document.body.appendChild(iframe);

        // Remove iframe after download starts
        setTimeout(() => {
            document.body.removeChild(iframe);
        }, 5000);
    }

    validate(): boolean {
        this.validation.name = !!this.model.name;
        this.validation.certificateTypeIds = this.model.certificateTypeIds && this.model.certificateTypeIds.length > 0;

        return this.validation.name && (this.validation.certificateTypeIds ?? false);
    }

    async save() {
        this.validationErrors = ResultError.createEmpty();

        if (!this.validate()) {
            toasts.failure('failure');
            return;
        }

        // Check if we need a template file
        if (!this.isEditMode && this.templateFiles.length === 0) {
            toasts.failure('Template file is required');
            return;
        }

        if (this.isEditMode && this.templateFiles.length === 0 && !this.existingFileUrl) {
            toasts.failure('Template file is required');
            return;
        }

        this.saving = true;

        try {
            const client = new SysAdminCertificateTypeTemplatesClient();

            // Create the request object
            const request = new UpsertCertificateTypeTemplateRequest({
                id: this.model.id,
                name: new MultilanguageModel({
                    et: this.model.name,
                    en: this.model.name // You might want to add proper multilanguage support
                }),
                certificateTypeIds: this.model.certificateTypeIds || [],
                description: new MultilanguageModel({
                    et: this.model.descriptionEt || '',
                    en: this.model.descriptionEn || ''
                }),
                templateVersion: this.model.version || '',
                templateFile: this.templateFiles.length > 0 ? this.templateFiles[0] : undefined,
                textLiterals: null
            });

            await client.upsert(request);

            const successMessage = this.isEditMode
                ? this.$ls.Administration_CertificateTypeTemplate_Success_Updated
                : this.$ls.Administration_CertificateTypeTemplate_Success_Created;

            toasts.success(successMessage);

            if (!this.isEditMode && this.templateFiles.length > 0) {
                toasts.success(cultureService.currentCulture === 'et-EE'
                    ? 'Tekstilised väärtused eraldati edukalt mallist'
                    : 'Text literals have been automatically extracted from your template');
            }

            // Navigate appropriately after save
            if (this.isEditMode) {
                // For edit mode, navigate back to detail view
                if (this.model.id) {
                    await this.$router.push({ name: 'certificate-type-template', params: { id: this.model.id } });
                }
            } else {
                // For create mode, navigate to list view
                await this.$router.push({ name: 'certificate-type-templates' });
            }
        } catch (error) {
            if (error instanceof ResultError) {
                this.validationErrors = error;
                toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
            } else {
                toasts.failure('failure');
                console.error('Error saving certificate type template:', error);
            }
        } finally {
            this.saving = false;
        }
    }

    async onActiveStateChange(newValue: boolean) {
        if (!this.isEditMode || !this.model.id) {
            return;
        }

        this.changingActiveState = true;
        try {
            const client = new SysAdminCertificateTypeTemplatesClient();

            if (newValue) {
                await client.activate(this.model.id);
                toasts.success(this.$ls.Administration_CertificateTypeTemplate_Success_Activated);
            } else {
                await client.deactivate(this.model.id);
                toasts.success(this.$ls.Administration_CertificateTypeTemplate_Success_Deactivated);
            }
        } catch (error) {
            // Revert the checkbox state on error
            this.model.isActive = !newValue;
            toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
            console.error('Error changing active state:', error);
        } finally {
            this.changingActiveState = false;
        }
    }
}
